import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import Header from '../../UI/Header/Header';
import MenuBlock from './MenuBlock';
import Circles from './Circles';
import {headerHeight} from '../../mixins';
import CircleMembers from './CircleMembers';
import {useDispatch, useSelector} from 'react-redux';
import Map from '../map/Map';
import {
    selectActiveCircle, selectBrowserCoords, selectShowDownload, selectShowDownloadModal,
    setActiveCircle, setActiveUser,
    setBrowserCoords, setDownloadNote, setShowDownloadModal,
    updateData, updateDataInBackground
} from './dashboardSlice';
import {
    allUsersIDs, circlesByID,
    ORMCircles, ORMUsers,

} from '../../app/models/ormSelectors';
import {selectToken} from '../auth/authSlice';
import RefreshButton from './RefreshButton';
import {
    getCurrentPosition,
    subscribeToFirebase,
    unSubscribeFromFirebase
} from '../../common/helper';
import API, {getGeoCode, getLocationByIP} from '../../common/api';
import {updateLatLngFromFirebase} from '../../app/models/LocationsModel';
import Modal from '../../UI/ModalPopup';
import {
    clearOnboardingData,
    selectPaymentStatus,
    selectPseudoFlow, selectPseudoInviteLink,
    selectSetShowPaywall, selectShowInviteModal,
    selectTrackingStatus, setShowInviteModal,
    setShowPaywall
} from '../onboarding/onboardingSlice';
import LocatingPopup from '../onboarding/LocatingPopup';
import PlansScreen from '../paywall/PlansScreen';
import QRInvite from '../invite/QRInvite';
import ErrorBoundary from '../../ErrorBoundary';
import {selectIsLogout} from '../login/loginSlice';
import InvitePopup from '../invite/InvitePopup';
import ModalPopup from '../../UI/ModalPopup';
import {selectInviteShow, setShowInvitePopup} from '../invite/inviteSlice';
import {selectIsIdle} from '../HOC/hocSlice';
import IdleDetection from '../HOC/IdleDetection';
import DownloadApp from './DownloadApp';
import DownloadCrouton from './DownloadCrouton';
import {sendLockerStudioData} from '../../analytics/lockerStudio';

const SHOW_PAYWALL_DELAY = 18000; //18 sec

const StyledDashboard = styled.div`
  margin-top: ${headerHeight};
  background-color: lightblue;
  width: 100%;
  height: calc(100% - ${headerHeight});
  position: relative;
`

const sendSubscribeRequest = async (token, IDs) => await API.subscribeRequest(token, IDs);
const sendUnsubscribeRequest = async (token, IDs) => await API.unsubscribeRequest(token, IDs);

const Dashboard = ({database}) => {
    const dispatch = useDispatch();
    const token = useSelector(selectToken);
    const ormCircles = useSelector(ORMCircles);
    const activeCircle = useSelector(selectActiveCircle);
    const circleByID = useSelector(circlesByID);
    const allIDs = useSelector(allUsersIDs);
    const [initCoords, setInitCoords] = useState(null);
    const openInvite = useSelector(selectInviteShow);
    const [country, setCountry] = useState('');
    const {browserLat, browserLng} = useSelector(selectBrowserCoords);
    const isPseudoFlow = useSelector(selectPseudoFlow);
    const [tracking, setTracking] = useState(!!isPseudoFlow);
    const pseudoTrackingDone = useSelector(selectTrackingStatus);
    const showPaywall = useSelector(selectSetShowPaywall);
    const paymentStatus = useSelector(selectPaymentStatus);
    const showInviteModal = useSelector(selectShowInviteModal);
    const pseudoInviteLink = useSelector(selectPseudoInviteLink);
    const ormUsers = useSelector(ORMUsers);
    const isUserPaid = useSelector(selectPaymentStatus);
    const isUserLoggedOut= useSelector(selectIsLogout);
    const [timeoutID, setTimeoutID] = useState(0);
    const isIdle = useSelector(selectIsIdle);
    const showDownloadCrouton = useSelector(selectShowDownload);
    const showDownload = useSelector(selectShowDownloadModal);
    const [eventSent, setEventSent] = useState(false);

    useEffect(() => {
        if (eventSent) return;
        sendLockerStudioData('web_app__screen_load', { screen__load: '' });
        setEventSent(true);
    }, [eventSent]);

    useEffect(() => {
        dispatch(updateData(token, false));
    }, [])

    const getInitialLocation = async () => {
        const country = await getLocationByIP();
        const coords = await getGeoCode(country);
        setCountry(country);

        return {lat: parseFloat(coords.lat), lng: parseFloat(coords.lon)}
    }

    const unsubscribeFromAllServices = () => {
        allIDs.forEach(i => unSubscribeFromFirebase(database, i, dispatch));
        sendUnsubscribeRequest(token, allIDs).then();
    }

    const subscribeToAllServices = () => {
        if (allIDs.length > 1) {
            sendSubscribeRequest(token, allIDs).then();
            allIDs.forEach(i => subscribeToFirebase(database, i, dispatch, updateLatLngFromFirebase));
        }
    }


    const onDataRefreshClick = () => {
        dispatch(updateData(token, true));
    };


    const copyTextCallback = () => {
        return navigator.clipboard.writeText(pseudoInviteLink);
    }

    useEffect(() => {
        if (isPseudoFlow && pseudoTrackingDone && !paymentStatus) {
            setTimeout(() => dispatch(setShowPaywall(true)), SHOW_PAYWALL_DELAY);
        }
    }, [isPseudoFlow, pseudoTrackingDone, paymentStatus])

    useEffect(() => {
        if (pseudoTrackingDone) {
            setTracking(false);
        }
    }, [pseudoTrackingDone])


    useEffect(() => {
        subscribeToAllServices();
    }, []);


    useEffect(() => {
        getInitialLocation().then(r => setInitCoords(r));
    }, []);

    useEffect(() => {
        getCurrentPosition(dispatch, setBrowserCoords);
    }, []);

    useEffect(() => {
        dispatch(setActiveCircle(ormCircles[0].id));
    }, [dispatch]);

    useEffect(() => {
        if (activeCircle) {
            const firstMember = circleByID(activeCircle)?.members[0];
            dispatch(setActiveUser(firstMember));
        }
    }, [activeCircle]);

    useEffect(() => {
        if (isPseudoFlow && ormUsers.length > 1) {
            dispatch(clearOnboardingData());
            setTimeout(() => location.reload(), 500); //redraw the map
        }
    }, [ormUsers, isPseudoFlow]);

    useEffect(() => {
        if (isUserPaid) {
            dispatch(updateDataInBackground(token, setTimeoutID)); //force BE data refresh for paid onboarding users
        }
    }, [isUserPaid, isIdle]);

    useEffect(() => {
        if (isIdle || isUserLoggedOut) {
            timeoutID && window.clearTimeout(timeoutID);
        }
    }, [timeoutID, isIdle, isUserLoggedOut]);

    const onDownloadCroutonClick = () => {
        dispatch(setShowDownloadModal(true))
        dispatch(setDownloadNote(false))
    }


    return (
        <IdleDetection onIdleHandler={unsubscribeFromAllServices} onActiveHandler={subscribeToAllServices}>
            <Header bgColor='#fff'>
                {activeCircle && <Circles/>}
                <MenuBlock/>
            </Header>
            <DownloadCrouton show={showDownloadCrouton} onCloseHandler={() => dispatch(setDownloadNote(false))} onClickHandler={onDownloadCroutonClick}/>
            <ErrorBoundary>
                <StyledDashboard>
                    <RefreshButton onDataRefreshClick={onDataRefreshClick}/>
                    {activeCircle && <CircleMembers/>}
                    {initCoords &&
                    <Map lat={browserLat || initCoords.lat} lng={browserLng || initCoords.lng} database={database}/>}
                </StyledDashboard>

                {!pseudoTrackingDone && (
                    <Modal show={tracking}
                           hideHeader={true}
                           body={<LocatingPopup country={country}/>}
                           hideButtons={true}
                           hideClose={true}
                    />
                )}
                <Modal show={showInviteModal}
                       onCloseClick={() => dispatch(setShowInviteModal(false))}
                       body={<QRInvite/>}
                       onActionClick={copyTextCallback}
                       btnText='Copy Link'
                />
                <Modal show={showDownload}
                       onCloseClick={() => dispatch(setShowDownloadModal(false))}
                       body={<DownloadApp/>}
                       hideButtons={true}
                />
                <ModalPopup show={openInvite} hideButtons={true} body={<InvitePopup/>} onCloseClick={() => dispatch(setShowInvitePopup(false))}/>
                {(isPseudoFlow && showPaywall) && <PlansScreen/>}
            </ErrorBoundary>
        </IdleDetection>
    )
};

export default Dashboard;


